import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Envelope, WhatsappLogo } from '@phosphor-icons/react';
import { LANDING_PAGES } from '../../data/landingPagesConfig';
import { SUB_SPECIALIZATIONS_BY_TYPE } from '../../data/clinicTypesConfig';
import logoIcon from '../../assets/logo-icon.png';
import './Footer.css';

const professionGroups = [
  {
    title: "בריאות וטיפול",
    items: [
      { key: "speech_therapist", label: "קלינאות תקשורת" },
      { key: "occupational_therapist", label: "ריפוי בעיסוק" },
      { key: "physiotherapy", label: "פיזיותרפיה" },
    ],
  },
  {
    title: "פסיכולוגיה וטיפול רגשי",
    items: [
      { key: "psychology", label: "פסיכולוגיה ופסיכותרפיה" },
      { key: "emotional_therapy", label: "טיפול רגשי" },
      { key: "clinical_social_work", label: "עבודה סוציאלית קלינית" },
    ],
  },
  {
    title: "ייעוץ ואימון",
    items: [
      { key: "parenting_counseling", label: "הדרכת הורים וייעוץ משפחתי" },
      { key: "personal_coaching", label: "אימון אישי" },
      { key: "couples_counseling", label: "ייעוץ זוגי ומיני" },
    ],
  },
  {
    title: "תזונה ורפואה משלימה",
    items: [
      { key: "nutrition", label: "תזונה ודיאטה" },
      { key: "naturopathy", label: "נטורופתיה ורפואה טבעית" },
      { key: "chinese_medicine", label: "רפואה סינית ומשלימה" },
    ],
  },
];

function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLandingPage = location.pathname === '/' || location.pathname.startsWith('/for/');

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        {/* Main Footer Content */}
        <div className="footer-content">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <div className="logo-wrapper">
                <span className="logo-text">CliniKit</span>
                <img src={logoIcon} alt="CliniKit" className="logo-icon" />
              </div>
            </Link>
            <p className="footer-tagline">פחות ניהול, יותר טיפול.</p>
            <p className="footer-description">
              הפלטפורמה המושלמת לניהול הקליניקה שלך - 
              לקוחות, פגישות, משימות והכל במקום אחד.
            </p>
          </div>

          <div className="footer-links">
            <div className="footer-links-column">
              <h4>ניווט מהיר</h4>
              <ul>
                <li>
                  <button onClick={() => scrollToSection('features')}>תכונות</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('pricing')}>מחירון</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('faq')}>שאלות נפוצות</button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')}>צור קשר</button>
                </li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h4>תוכן</h4>
              <ul>
                <li><Link to="/blog">מאמרים ומדריכים</Link></li>
              </ul>
              <h4 style={{ marginTop: 'var(--spacing-lg)' }}>משפטי</h4>
              <ul>
                <li><Link to="/terms-of-service">תנאי שימוש</Link></li>
                <li><Link to="/privacy-policy">מדיניות פרטיות</Link></li>
                <li><Link to="/pay/terms">תנאי תשלום</Link></li>
                <li><Link to="/accessibility">הצהרת נגישות</Link></li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h4>יצירת קשר</h4>
              <ul className="contact-info">
                <li>
                  <span className="contact-icon">
                    <Envelope size={18} weight="regular" />
                  </span>
                  <span>office@clinikit.co.il</span>
                </li>
                <li>
                  <a
                    href="https://wa.me/972543073737?text=%D7%94%D7%99%D7%99%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C%20CliniKit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    <span className="contact-icon">
                      <WhatsappLogo size={18} weight="fill" />
                    </span>
                    <span>דברו איתנו ב-WhatsApp</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Profession Landing Pages Links */}
        {isLandingPage && (
          <div className="footer-professions">
            <h4 className="footer-professions-title">
              CliniKit מתאימה למגוון מקצועות טיפול
            </h4>

            <div className="footer-professions-grid">
              {professionGroups.map((group) => (
                <div key={group.title} className="footer-profession-group">
                  <h5>{group.title}</h5>
                  {group.items.map((item) => {
                    const page = LANDING_PAGES[item.key];
                    const subs = SUB_SPECIALIZATIONS_BY_TYPE[item.key] || [];
                    if (!page) return null;

                    return (
                      <div key={item.key} className="footer-profession-item">
                        <Link
                          to={`/for/${page.slug}`}
                          className="footer-profession-link"
                        >
                          {item.label}
                        </Link>
                        {subs.length > 0 && (
                          <ul className="footer-subspecs">
                            {subs.map((sub) => (
                              <li key={sub.value}>
                                <Link to={`/for/${page.slug}#specializations`}>
                                  {sub.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} CliniKit. כל הזכויות שמורות.</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
